import { useAnalytics } from '@/hooks/use-analytics';
import { t } from '@/lib/i18n/messages';
import type { Locale } from '@/types/auth';
import type {
  WalkInitiativeStats,
  WalkParticipant,
  TypedParticipant,
} from '@/types/analytics';

interface TypeAnalyticsSectionProps {
  initiativeId: string;
  initiativeType: string;
  locale: Locale;
}

interface StatCard {
  label: string;
  value: string | number;
}

function SkeletonCards() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="bg-card rounded-lg border border-border p-6 animate-pulse"
        >
          <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-3/4 mb-3" />
          <div className="h-7 bg-slate-200 dark:bg-slate-700 rounded w-1/2" />
        </div>
      ))}
    </div>
  );
}

function StatCardGrid({ cards, isRTL }: { cards: StatCard[]; isRTL: boolean }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {cards.map((card) => (
        <div
          key={card.label}
          className="bg-card rounded-lg border border-border p-6"
        >
          <p className={`text-xs font-medium text-foreground-secondary mb-2 uppercase tracking-wide ${isRTL ? "text-right" : ""}`}>
            {card.label}
          </p>
          <p className={`text-2xl font-bold text-foreground ${isRTL ? "text-right" : ""}`}>
            {card.value}
          </p>
        </div>
      ))}
    </div>
  );
}

interface ParticipantRow {
  user_id: string;
  name: string;
  avatar: string | null;
  metric: string;
  metricValue: string | number;
}

function getParticipantRows(
  participants: TypedParticipant[],
  initiativeType: string,
  locale: Locale
): ParticipantRow[] {
  return participants.map((p) => {
    let metric = '';
    let metricValue: string | number = '';

    if (initiativeType === 'walk_initiative') {
      const wp = p as WalkParticipant;
      metric = t(locale, 'totalSteps');
      metricValue = wp.steps?.toLocaleString() ?? '—';
    }

    return {
      user_id: p.user_id,
      name: p.name,
      avatar: p.avatar,
      metric,
      metricValue,
    };
  });
}

function ParticipantsTable({ participants, initiativeType, locale }: {
  participants: TypedParticipant[];
  initiativeType: string;
  locale: Locale;
}) {
  const isRTL = locale === 'ar';
  const rows = getParticipantRows(participants, initiativeType, locale);

  if (rows.length === 0) return null;

  return (
    <div>
      <h4 className={`text-sm font-semibold text-slate-700 mb-3 ${isRTL ? 'text-right' : ''}`}>
        {locale === 'ar' ? 'المشاركون' : 'Participants'}
      </h4>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted">
              <th className={`px-4 py-3 font-medium text-foreground-secondary ${isRTL ? 'text-right' : 'text-left'}`}>
                {locale === 'ar' ? 'الاسم' : 'Name'}
              </th>
              <th className={`px-4 py-3 font-medium text-foreground-secondary ${isRTL ? 'text-right' : 'text-left'}`}>
                {rows[0]?.metric}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.user_id} className={`border-t border-border ${i % 2 === 0 ? '' : 'bg-slate-50/50/30'}`}>
                <td className="px-4 py-3">
                  <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-bold flex-shrink-0 overflow-hidden">
                      {row.avatar ? (
                        <img src={row.avatar} alt={row.name} className="w-full h-full object-cover" />
                      ) : (
                        row.name.charAt(0).toUpperCase()
                      )}
                    </div>
                    <span className="text-slate-800 font-medium">{row.name}</span>
                  </div>
                </td>
                <td className={`px-4 py-3 text-slate-700 font-semibold ${isRTL ? 'text-right' : ''}`}>
                  {row.metricValue}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function getWalkCards(stats: WalkInitiativeStats, locale: Locale): StatCard[] {
  return [
    { label: t(locale, 'totalSteps'), value: stats.total_steps.toLocaleString() },
    { label: t(locale, 'totalDistance'), value: stats.total_distance.toLocaleString() },
    { label: t(locale, 'avgSteps'), value: stats.average_steps_per_participant },
    { label: t(locale, 'avgDistance'), value: stats.average_distance_per_participant },
    { label: t(locale, 'highestSteps'), value: stats.highest_steps.toLocaleString() },
    {
      label: locale === 'ar' ? 'أعلى مسافة' : 'Highest Distance',
      value: stats.highest_distance.toLocaleString(),
    },
  ];
}

const KNOWN_TYPES = ['walk_initiative'];

export function TypeAnalyticsSection({
  initiativeId,
  initiativeType,
  locale,
}: TypeAnalyticsSectionProps) {
  const isRTL = locale === 'ar';
  const { typedStats, participants, loading, error } = useAnalytics(initiativeId, initiativeType);

  if (!KNOWN_TYPES.includes(initiativeType)) {
    return null;
  }

  return (
    <div className="bg-card rounded-lg border border-border p-6 space-y-6">
      <h3 className={`text-base font-semibold text-foreground ${isRTL ? "text-right" : ""}`}>
        {t(locale, 'walkStatsTitle')}
      </h3>

      {loading && <SkeletonCards />}

      {!loading && error && (
        <p className={`text-sm text-foreground-secondary ${isRTL ? "text-right" : ""}`}>
          {t(locale, 'analyticsUnavailable')}
        </p>
      )}

      {!loading && !error && !typedStats && (
        <p className={`text-sm text-foreground-secondary ${isRTL ? "text-right" : ""}`}>
          {t(locale, 'noDataAvailable')}
        </p>
      )}

      {!loading && !error && typedStats && (
        <div className="space-y-6">
          <StatCardGrid cards={getWalkCards(typedStats as WalkInitiativeStats, locale)} isRTL={isRTL} />
          <ParticipantsTable
            participants={participants}
            initiativeType={initiativeType}
            locale={locale}
          />
        </div>
      )}
    </div>
  );
}
